import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { formatUTCToLocalDateSlash, getLocaleByLang } from '../utils/timezone';
import { formatCurrency } from '../utils/currency';
import { canDownloadFacture } from '../utils/permissionConfig';
import { downloadInvoicePdf, getOptionalInvoiceReference } from '../services/accountingDocuments';
import { loadAccountingInvoiceReferences } from '../services/accountingInvoiceReferenceApi';
import styles from './ManualReservationList.module.css';
import CsvPreviewModal from './CsvPreviewModal';

// 선금 리스트 관련 텍스트
const TEXT = {
    depositList: {
        ko: '선금 리스트',
        en: 'Deposit List',
        fr: 'Liste des acomptes'
    },
    noDeposits: {
        ko: '해당 월에 받은 선금이 없습니다',
        en: 'No deposits received this month',
        fr: 'Aucun acompte reçu ce mois'
    },
    loading: {
        ko: '로딩 중...',
        en: 'Loading...',
        fr: 'Chargement...'
    },
    depositDate: {
        ko: '선금일',
        en: 'Deposit Date',
        fr: 'Date acompte'
    },
    reservationDate: {
        ko: '예약일',
        en: 'Reservation Date',
        fr: 'Date réservation'
    },
    totalDeposits: {
        ko: '총 선금',
        en: 'Total Deposits',
        fr: 'Total acomptes'
    },
    newestFirst: {
        ko: '최신순',
        en: 'Newest',
        fr: 'Récent'
    },
    oldestFirst: {
        ko: '오래된순',
        en: 'Oldest',
        fr: 'Ancien'
    }
};

function DepositList({
    reservations,
    selectedYear,
    selectedMonth,
    exchangeRates,
    lang,
    loading,
    onOpenModal,
    userInfo
}) {
    const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = 최신순, 'asc' = 오래된순
    const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);
    const [csvPreviewData, setCsvPreviewData] = useState([]);
    const canExportFacture = canDownloadFacture(userInfo);

    // 선금 받은 날짜 기준으로 필터링
    const getFilteredDeposits = () => {
        if (!reservations || reservations.length === 0) return [];

        return reservations.filter(r => {
            // 선금 받은 예약만
            if (!r.deposit_received || !r.deposit_received_date) return false;

            // deposit_received_date 기준으로 년/월 필터링
            const depositDT = DateTime.fromISO(r.deposit_received_date, { zone: 'utc' }).setZone('Europe/Paris');
            const year = depositDT.year;
            const month = depositDT.month;

            return year === selectedYear && month === selectedMonth;
        }).sort((a, b) => {
            // 정렬 순서에 따라 정렬
            const dateA = new Date(a.deposit_received_date);
            const dateB = new Date(b.deposit_received_date);
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
    };

    const filteredDeposits = getFilteredDeposits();

    // 총 선금액 계산 (유로 환산)
    const getTotalDeposits = () => {
        return reservations?.periodTotals?.[`${selectedYear}-${String(selectedMonth).padStart(2, '0')}`]?.deposits || 0;
    };

    // 플랫폼 표시
    const getPlatformLabel = (platform) => {
        const platformMap = {
            paypal: 'PayPal',
            lydia: 'Lydia',
            cash: lang === 'ko' ? '현금' : lang === 'fr' ? 'Espèces' : 'Cash',
            bank_transfer: lang === 'ko' ? '계좌이체' : lang === 'fr' ? 'Virement' : 'Bank Transfer',
            other: lang === 'ko' ? '기타' : lang === 'fr' ? 'Autre' : 'Other'
        };
        return platformMap[platform] || platform || '-';
    };

    // 플랫폼 CSV용 표시 (고정 영어)
    const getPlatformLabelForCSV = (platform) => {
        const platformMap = {
            paypal: 'PayPal',
            lydia: 'Lydia',
            cash: 'Espèces',
            bank_transfer: 'Virement',
            other: 'Autre'
        };
        return platformMap[platform] || platform || '-';
    };

    // CSV 미리보기 열기
    const openCsvPreview = async () => {
        if (filteredDeposits.length === 0) {
            alert('No data to preview');
            return;
        }

        // 선금일 기준으로 정렬
        const sortedByDate = [...filteredDeposits].sort((a, b) =>
            new Date(a.deposit_received_date) - new Date(b.deposit_received_date)
        );

        // 미리보기 데이터 생성
        const data = sortedByDate.map((r, index) => {
            const depositDT = DateTime.fromISO(r.deposit_received_date, { zone: 'utc' }).setZone('Europe/Paris');
            const ttc = parseFloat(r.deposit_amount) || 0;
            const ht = ttc / 1.20;
            const tva = ttc - ht;

            return {
                id: r.id,
                source_type: 'deposit',
                source_id: r.id,
                payment_date: depositDT.toISODate(),
                payment_date_original: depositDT.toISODate(),
                date: depositDT.toFormat('dd/MM/yyyy'),
                type: 'Acompte',
                ttc: ttc.toFixed(2),
                tva: tva.toFixed(2),
                ht: ht.toFixed(2),
                moyen: getPlatformLabelForCSV(r.deposit_platform),
                client: r.client_instagram || r.client_name || '-',
                refYear: depositDT.year,
                refMonth: String(depositDT.month).padStart(2, '0')
            };
        });

        try {
            setCsvPreviewData(await loadAccountingInvoiceReferences(data));
            setCsvPreviewOpen(true);
        } catch (error) {
            console.error('Failed to load deposit invoice references:', error);
            alert('Impossible de charger les numéros de facture.');
        }
    };

    // CSV 다운로드 핸들러
    const handleCsvDownload = (selectedData, selectedRows, allData) => {
        // CSV 헤더
        const headers = ['Date', 'Type', 'TTC', 'TVA', 'HT', 'Moyen', 'Client', 'Référence'];

        // 선택된 데이터로 CSV 생성
        const csvData = selectedData.map((r) => {
            const reference = getOptionalInvoiceReference(r, '');
            return [
                r.date,
                r.type,
                r.ttc,
                r.tva,
                r.ht,
                r.moyen,
                r.client,
                reference
            ];
        });

        // CSV 문자열 생성
        const csvContent = [headers, ...csvData]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        // BOM 추가
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

        // 파일명 생성
        const filename = `acomptes_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`;

        // 다운로드
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Facture PDF 다운로드 핸들러
    const handleFactureDownload = (selectedData) => {
        if (!canExportFacture) return;
        const issuedData = selectedData.filter(row => row.invoice_reference);
        if (issuedData.length === 0) {
            alert('Aucune facture émise depuis les crédits rapprochés.');
            return;
        }
        downloadInvoicePdf(issuedData, { type: 'deposit', year: selectedYear, month: selectedMonth });
    };

    if (loading) {
        return <div className={styles.emptyState}>{TEXT.loading[lang]}</div>;
    }

    return (
        <div>
            {/* 총 선금 요약 */}
            {filteredDeposits.length > 0 && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    background: 'transparent',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    border: '1px solid #ddd'
                }}>
                    <span style={{ fontSize: '13px', color: '#495057', fontWeight: '500' }}>
                        {TEXT.totalDeposits[lang]}:
                    </span>
                    <span style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>
                        {filteredDeposits.length}{lang === 'ko' ? '건' : ''} / {getTotalDeposits().toFixed(0)}€
                    </span>
                </div>
            )}

            {/* 정렬 토글 */}
            {filteredDeposits.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                    <button
                        onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            background: 'transparent',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#555',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {sortOrder === 'desc' ? (
                                <>
                                    <path d="M12 5v14" />
                                    <path d="m19 12-7 7-7-7" />
                                </>
                            ) : (
                                <>
                                    <path d="M12 19V5" />
                                    <path d="m5 12 7-7 7 7" />
                                </>
                            )}
                        </svg>
                        {sortOrder === 'desc' ? TEXT.newestFirst[lang] : TEXT.oldestFirst[lang]}
                    </button>
                </div>
            )}

            {/* 선금 리스트 */}
            {filteredDeposits.length === 0 ? (
                <div className={styles.emptyState}>{TEXT.noDeposits[lang]}</div>
            ) : (
                <div className={styles.cardList}>
                    {filteredDeposits.map(r => {
                        const depositDate = r.deposit_received_date
                            ? formatUTCToLocalDateSlash(r.deposit_received_date, getLocaleByLang(lang))
                            : '-';

                        // 예약일 (이벤트 시작일)
                        let eventDateStr = '-';
                        if (r.relatedEvent?.start_time) {
                            try {
                                const eventTz = r.relatedEvent?.calendar_type === 'personal' && r.relatedEvent?.event_timezone
                                    ? r.relatedEvent.event_timezone
                                    : 'Europe/Paris';
                                const startDT = DateTime.fromISO(r.relatedEvent.start_time, { zone: 'utc' }).setZone(eventTz);
                                eventDateStr = startDT.toFormat('dd/MM/yyyy');
                            } catch { eventDateStr = '-'; }
                        }

                        return (
                            <div
                                key={r.id}
                                className={styles.reservationCard}
                                onClick={() => onOpenModal(r)}
                            >
                                {/* 왼쪽: 고객 정보 */}
                                <div className={styles.cardLeft}>
                                    <div className={styles.cardHeader}>
                                        <span className={styles.cardClientName}>
                                            {r.client_instagram || r.client_name || '-'}
                                        </span>
                                        {r.artist_name && (
                                            <>
                                                <span className={styles.cardArrow}>→</span>
                                                <span className={styles.cardArtistName}>{r.artist_name}</span>
                                            </>
                                        )}
                                    </div>
                                    <div className={styles.cardMeta}>
                                        <span className={styles.cardMetaItem} title={TEXT.depositDate[lang]}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '3px', verticalAlign: 'middle' }}>
                                                <circle cx="12" cy="12" r="10" />
                                                <path d="M12 6v6l4 2" />
                                            </svg>
                                            {depositDate}
                                        </span>
                                        <span className={styles.cardMetaItem}>
                                            {getPlatformLabel(r.deposit_platform)}
                                        </span>
                                        <span className={styles.cardMetaItem} title={TEXT.reservationDate[lang]}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '3px', verticalAlign: 'middle' }}>
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                            {eventDateStr}
                                        </span>
                                    </div>
                                </div>

                                {/* 오른쪽: 금액 */}
                                <div className={styles.cardRight}>
                                    <span className={styles.cardPrice} style={{ color: '#2e7d32' }}>
                                        {r.deposit_amount}{r.currency && r.currency !== 'EUR' ? ` ${r.currency}` : '€'}
                                    </span>
                                    {/* 유로 환산 표시 */}
                                    {r.currency && r.currency !== 'EUR' && exchangeRates && (
                                        <span style={{ fontSize: '10px', color: '#888' }}>
                                            ≈ {formatCurrency(r.accounting?.deposit_eur, 'EUR')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 버튼 영역 - 선금 탭 전용 */}
            {filteredDeposits.length > 0 && (
                <div style={{
                    marginTop: '20px',
                    marginBottom: '20px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px'
                }}>
                    {/* 회계용 CSV 다운로드 버튼 */}
                    <button
                        onClick={openCsvPreview}
                        style={{
                            padding: '4px 8px',
                            backgroundColor: '#2e7d32',
                            color: '#ffffff',
                            border: '1px solid #2e7d32',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '400',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            minWidth: '100px',
                            textTransform: 'uppercase'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#1b5e20';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#2e7d32';
                        }}
                    >
                        {lang === 'ko' ? '회계용 CSV' : lang === 'fr' ? 'CSV Comptable' : 'Accounting CSV'}
                    </button>
                </div>
            )}

            {/* CSV 미리보기 모달 */}
            <CsvPreviewModal
                isOpen={csvPreviewOpen}
                onClose={() => setCsvPreviewOpen(false)}
                data={csvPreviewData}
                onDownloadCsv={handleCsvDownload}
                onDownloadFacture={handleFactureDownload}
                showFactureButton={canExportFacture}
                lang={lang}
                refPrefix="A"
                draftType="deposit"
                draftYear={selectedYear}
                draftMonth={selectedMonth}
            />
        </div>
    );
}

export default DepositList;
